import React from 'react';
import {
  Document,
  PDFViewer,
  Page as PDFPage,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  StyleSheet,
  Font
} from '@react-pdf/renderer';

// Use Helvetica font (standard PDF font)
Font.register({
  family: 'Helvetica',
  src: 'standard'
});

// Create styles using StyleSheet.create
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 9
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: '2px solid #1976d2'
  },
  logo: {
    width: 80,
    height: 80
  },
  headerCenter: {
    alignItems: 'center',
    flex: 1
  },
  hospitalName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 3
  },
  hospitalAddress: {
    fontSize: 8,
    color: '#666'
  },
  reportTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 5
  },
  reportSubtitle: {
    fontSize: 10,
    color: '#666',
    marginTop: 3
  },

  // Patient Information
  patientContainer: {
    marginBottom: 15,
    padding: 10,
    border: '1px solid #ccc',
    borderRadius: 5,
    backgroundColor: '#f9f9f9'
  },
  patientRow: {
    flexDirection: 'row',
    marginBottom: 4
  },
  patientLabel: {
    width: 100,
    fontWeight: 'bold',
    color: '#333'
  },
  patientValue: {
    flex: 1,
    color: '#555'
  },
  patientDoubleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  patientColumn: {
    width: '48%'
  },

  // Section Headers
  sectionHeader: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: 5,
    marginVertical: 10,
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 'bold'
  },

  // Tables
  table: {
    marginBottom: 15,
    border: '1px solid #ccc'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #ccc'
  },
  tableHeader: {
    backgroundColor: '#e3f2fd',
    fontWeight: 'bold'
  },
  tableCell: {
    padding: 4,
    borderRight: '1px solid #ccc',
    textAlign: 'center'
  },
  tableCellLeft: {
    padding: 4,
    borderRight: '1px solid #ccc',
    textAlign: 'left',
    paddingLeft: 6
  },

  // Column widths for OPD Registration
  srNoCell: { width: '5%' },
  dateCell: { width: '10%' },
  opNoCell: { width: '15%' },
  serviceCell: { width: '25%' },
  regCell: { width: '10%' },
  discCell: { width: '10%' },
  totalCell: { width: '10%' },

  // Column widths for Billing - Service Items
  billNoCell: { width: '12%' },
  serviceDescCell: { width: '20%' },
  qtyCell: { width: '6%' },
  rateCell: { width: '10%' },
  amountCell: { width: '10%' },
  itemDiscCell: { width: '8%' },
  gstCell: { width: '8%' },
  netCell: { width: '12%' },

  // Bill Summary Row
  billSummaryRow: {
    backgroundColor: '#f0f7ff',
    fontWeight: 'bold',
    borderTop: '1px solid #1976d2'
  },
  billSummaryLabel: {
    padding: 4,
    textAlign: 'left',
    paddingLeft: 10
  },
  billSummaryValue: {
    padding: 4,
    textAlign: 'center'
  },

  // Summary Section
  summaryContainer: {
    marginTop: 20,
    padding: 10,
    border: '1px solid #ccc',
    backgroundColor: '#f5f5f5'
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  summaryLabel: {
    fontWeight: 'bold',
    color: '#555'
  },
  summaryValue: {
    fontWeight: 'bold',
    color: '#333'
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '2px solid #1976d2',
    fontSize: 10
  },
  grandTotal: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1976d2'
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    fontSize: 7,
    color: '#666',
    textAlign: 'center',
    borderTop: '1px solid #eee',
    paddingTop: 10
  }
});

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return 'N/A';
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  return timeString;
};

const calculateAge = (dobString) => {
  if (!dobString) return '';
  try {
    const dob = new Date(dobString);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return `${age} Years`;
  } catch {
    return '';
  }
};

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'Rs. 0.00';
  return `Rs. ${parseFloat(amount).toFixed(2)}`;
};

// Process data for calculations - Updated to handle bill grouping
// Process data for calculations - Updated to handle bill grouping
const processLedgerData = (data) => {
  let opdTotal = 0;
  let totalBillAmount = 0; // Total before any discounts
  let netBillingAmount = 0; // Total after all discounts
  let totalBillDiscount = 0;
  let totalItemDiscount = 0;
  let totalGST = 0;
  let allBills = [];

  data.forEach(item => {
    // OPD Registration
    if (item.opd && item.opd.total) {
      opdTotal += item.opd.total;
    }

    // Process billing details - Group by bill
    if (item.billing_details && item.billing_details.length > 0) {
      const billMap = {};

      // Group service items by bill
      item.billing_details.forEach(bill => {
        const billId = bill._id;

        if (!billMap[billId]) {
          // Calculate bill totals
          const billNetAmount = bill.net_amt || 0;
          const billTotalAmount = bill.total_amt || 0;
          const billDiscount = bill.disc_amt || 0;
          const billGST = bill.gst_amt || 0;

          billMap[billId] = {
            billNo: bill.bill_no,
            date: bill.date,
            time: bill.time,
            totalAmt: billTotalAmount,
            billDiscAmt: billDiscount,
            gstAmt: billGST,
            netAmt: billNetAmount,
            totalQty: bill.total_qty || 0,
            payMode: bill.pay_mode || '',
            serviceItems: []
          };

          // Add to totals
          totalBillAmount += billTotalAmount;
          netBillingAmount += billNetAmount;
          totalBillDiscount += billDiscount;
          totalGST += billGST;
        }

        // Check if service_items is an object
        if (bill.service_items) {
          const service = bill.service_items;
          const amount = (service.rate || 0) * (service.quantity || 1);
          const itemDiscount = service.disc_amt || 0;

          billMap[billId].serviceItems.push({
            serviceName: service.service_name,
            doctor: service.doctor,
            quantity: service.quantity || 1,
            rate: service.rate || 0,
            amount: amount,
            itemDiscount: itemDiscount,
            gst: service.gst_amt || 0,
            netAmount: service.net_amt || 0
          });

          totalItemDiscount += itemDiscount;
        }
      });

      // Convert bill map to array
      Object.values(billMap).forEach(bill => {
        allBills.push(bill);
      });
    }
  });

  const grandTotal = opdTotal + netBillingAmount;

  return {
    opdTotal,
    totalBillAmount,
    netBillingAmount,
    grandTotal,
    allBills,
    totalBillDiscount,
    totalItemDiscount,
    totalGST
  };
};

export const OpdPatientLedgerPdf = ({ data = [] }) => {
  // If no data
  if (!data || data.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'white'
      }}>
        <PDFViewer width="100%" height="100%">
          <Document title="OPD Patient Ledger Report">
            <PDFPage size="A4" style={styles.page}>
              <PDFText style={{ textAlign: 'center', marginTop: 50 }}>
                No data available for the selected criteria
              </PDFText>
            </PDFPage>
          </Document>
        </PDFViewer>
      </div>
    );
  }

  // Get calculations
  const {
    opdTotal,
    totalBillAmount,
    netBillingAmount,
    grandTotal,
    allBills,
    totalBillDiscount,
    totalItemDiscount,
    totalGST
  } = processLedgerData(data);

  // Get patient info from first item
  const patientInfo = data[0]?.opd;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'white'
    }}>
      <PDFViewer width="100%" height="100%">
        <Document title="OPD Patient Ledger Report">
          <PDFPage size="A4" style={styles.page}>

            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
            />

            {/* Patient Information */}
            {patientInfo && (
              <PDFView style={styles.patientContainer}>
                <PDFView style={styles.patientDoubleRow}>
                  <PDFView style={styles.patientColumn}>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>UHID:</PDFText>
                      <PDFText style={styles.patientValue}>{patientInfo.uhid || 'N/A'}</PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>OP No:</PDFText>
                      <PDFText style={styles.patientValue}>{patientInfo.op_no || 'N/A'}</PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Patient Name:</PDFText>
                      <PDFText style={styles.patientValue}>
                        {`${patientInfo.name_suffix || ''} ${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim() || 'N/A'}
                      </PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Guardian:</PDFText>
                      <PDFText style={styles.patientValue}>{patientInfo.guardian_name || 'N/A'}</PDFText>
                    </PDFView>
                  </PDFView>
                  <PDFView style={styles.patientColumn}>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Age:</PDFText>
                      <PDFText style={styles.patientValue}>{calculateAge(patientInfo.dob)}</PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Gender:</PDFText>
                      <PDFText style={styles.patientValue}>{patientInfo.gender || 'N/A'}</PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Mobile:</PDFText>
                      <PDFText style={styles.patientValue}>{patientInfo.address?.mobile || 'N/A'}</PDFText>
                    </PDFView>
                    <PDFView style={styles.patientRow}>
                      <PDFText style={styles.patientLabel}>Sponsor:</PDFText>
                      <PDFText style={styles.patientValue}>{data[0]?.name || patientInfo.sponsor?.name || 'N/A'}</PDFText>
                    </PDFView>
                  </PDFView>
                </PDFView>
                <PDFView style={styles.patientRow}>
                  <PDFText style={styles.patientLabel}>Address:</PDFText>
                  <PDFText style={styles.patientValue}>{patientInfo.address?.address || 'N/A'}</PDFText>
                </PDFView>
              </PDFView>
            )}

            {/* OPD Registration Section */}
            <PDFText style={styles.sectionHeader}>OPD REGISTRATION</PDFText>
            <PDFView style={styles.table}>
              {/* Table Header */}
              <PDFView style={[styles.tableRow, styles.tableHeader]}>
                <PDFText style={[styles.tableCell, styles.srNoCell]}>Sr. No.</PDFText>
                <PDFText style={[styles.tableCell, styles.dateCell]}>Date</PDFText>
                <PDFText style={[styles.tableCell, styles.opNoCell]}>OP No</PDFText>
                <PDFText style={[styles.tableCellLeft, styles.serviceCell]}>Service</PDFText>
                <PDFText style={[styles.tableCell, styles.regCell]}>Registration</PDFText>
                <PDFText style={[styles.tableCell, styles.discCell]}>Discount</PDFText>
                <PDFText style={[styles.tableCell, styles.totalCell]}>Total</PDFText>
              </PDFView>

              {/* OPD Registration Row */}
              {data.map((item, index) => {
                const patient = item.opd;
                return (
                  <PDFView key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }]}>
                    <PDFText style={[styles.tableCell, styles.srNoCell]}>{index + 1}</PDFText>
                    <PDFText style={[styles.tableCell, styles.dateCell]}>{formatDate(patient.date)}</PDFText>
                    <PDFText style={[styles.tableCell, styles.opNoCell]}>{patient.op_no || 'N/A'}</PDFText>
                    <PDFText style={[styles.tableCellLeft, styles.serviceCell]}>OPD Registration & Consultation</PDFText>
                    <PDFText style={[styles.tableCell, styles.regCell]}>{formatCurrency(patient.reg)}</PDFText>
                    <PDFText style={[styles.tableCell, styles.discCell]}>{formatCurrency(patient.disc)}</PDFText>
                    <PDFText style={[styles.tableCell, styles.totalCell]}>{formatCurrency(patient.total)}</PDFText>
                  </PDFView>
                );
              })}
            </PDFView>

            {/* OPD Billing Section */}
            <PDFText style={styles.sectionHeader}>OPD BILLING DETAILS</PDFText>
            {allBills.length > 0 ? (
              allBills.map((bill, billIndex) => (
                <PDFView key={billIndex} style={{ marginBottom: 15 }}>
                  {/* Bill Header */}
                  <PDFView style={{
                    backgroundColor: '#e8f4fd',
                    padding: 6,
                    marginBottom: 5,
                    border: '1px solid #b3d9ff',
                    borderRadius: 3
                  }}>
                    <PDFView style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <PDFText style={{ fontWeight: 'bold', fontSize: 9 }}>
                        Bill No: {bill.billNo} | Date: {formatDate(bill.date)} {bill.time ? ` | Time: ${formatTime(bill.time)}` : ''} | Payment Mode: {bill.payMode}
                      </PDFText>
                      <PDFText style={{ fontWeight: 'bold', fontSize: 9 }}>
                        Total Items: {bill.totalQty}
                      </PDFText>
                    </PDFView>
                  </PDFView>

                  {/* Service Items Table */}
                  <PDFView style={styles.table}>
                    {/* Table Header */}
                    <PDFView style={[styles.tableRow, styles.tableHeader]}>
                      <PDFText style={[styles.tableCell, styles.srNoCell]}>Sr.</PDFText>
                      <PDFText style={[styles.tableCellLeft, styles.serviceDescCell]}>Service Description</PDFText>
                      <PDFText style={[styles.tableCell, styles.qtyCell]}>Qty</PDFText>
                      <PDFText style={[styles.tableCell, styles.rateCell]}>Rate</PDFText>
                      <PDFText style={[styles.tableCell, styles.amountCell]}>Amount</PDFText>
                      <PDFText style={[styles.tableCell, styles.itemDiscCell]}>Item Disc</PDFText>
                      <PDFText style={[styles.tableCell, styles.gstCell]}>GST</PDFText>
                      <PDFText style={[styles.tableCell, styles.netCell]}>Net Amount</PDFText>
                    </PDFView>

                    {/* Service Items */}
                    {bill.serviceItems.map((item, itemIndex) => (
                      <PDFView key={itemIndex} style={[styles.tableRow, { backgroundColor: itemIndex % 2 === 0 ? '#ffffff' : '#f9f9f9' }]}>
                        <PDFText style={[styles.tableCell, styles.srNoCell]}>{itemIndex + 1}</PDFText>
                        <PDFText style={[styles.tableCellLeft, styles.serviceDescCell]}>
                          {item.serviceName || 'N/A'}
                        </PDFText>
                        <PDFText style={[styles.tableCell, styles.qtyCell]}>{item.quantity}</PDFText>
                        <PDFText style={[styles.tableCell, styles.rateCell]}>{formatCurrency(item.rate)}</PDFText>
                        <PDFText style={[styles.tableCell, styles.amountCell]}>{formatCurrency(item.amount)}</PDFText>
                        <PDFText style={[styles.tableCell, styles.itemDiscCell]}>{formatCurrency(item.itemDiscount)}</PDFText>
                        <PDFText style={[styles.tableCell, styles.gstCell]}>{formatCurrency(item.gst)}</PDFText>
                        <PDFText style={[styles.tableCell, styles.netCell]}>{formatCurrency(item.netAmount)}</PDFText>
                      </PDFView>
                    ))}

                    {/* Bill Summary Row */}
                    <PDFView style={[styles.tableRow, styles.billSummaryRow]}>
                      <PDFText style={[styles.billSummaryLabel, { width: '25%' }]}>Bill Summary:</PDFText>
                      <PDFText style={[styles.billSummaryValue, { width: '10%' }]}>{formatCurrency(bill.totalAmt)}</PDFText>
                      <PDFText style={[styles.billSummaryValue, { width: '8%' }]}>{formatCurrency(bill.billDiscAmt)}</PDFText>
                      <PDFText style={[styles.billSummaryValue, { width: '8%' }]}>{formatCurrency(bill.gstAmt)}</PDFText>
                      <PDFText style={[styles.billSummaryValue, { width: '12%', fontWeight: 'bold', color: '#1976d2' }]}>
                        {formatCurrency(bill.netAmt)}
                      </PDFText>
                      <PDFText style={[styles.billSummaryLabel, { width: '15%', textAlign: 'right', paddingRight: 10 }]}>
                        Bill Net Total:
                      </PDFText>
                      <PDFText style={[styles.billSummaryValue, { width: '12%', fontWeight: 'bold', color: '#d32f2f' }]}>
                        {formatCurrency(bill.netAmt)}
                      </PDFText>
                    </PDFView>
                  </PDFView>
                </PDFView>
              ))
            ) : (
              <PDFView style={{ padding: 20, border: '1px solid #ccc', marginBottom: 15 }}>
                <PDFText style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                  No billing records found for this period
                </PDFText>
              </PDFView>
            )}

            {/* Financial Summary */}

            <PDFView style={styles.summaryContainer}>
              <PDFText style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 10, color: '#1976d2' }}>
                FINANCIAL SUMMARY
              </PDFText>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>OPD Registration Total:</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(opdTotal)}</PDFText>
              </PDFView>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>Total Bill Amount (Before Discounts):</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(totalBillAmount)}</PDFText>
              </PDFView>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>Total Item Discounts:</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(totalItemDiscount)}</PDFText>
              </PDFView>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>Total Bill Discounts:</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(totalBillDiscount)}</PDFText>
              </PDFView>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>Total GST:</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(totalGST)}</PDFText>
              </PDFView>

              <PDFView style={styles.summaryRow}>
                <PDFText style={styles.summaryLabel}>Net Billing Amount:</PDFText>
                <PDFText style={styles.summaryValue}>{formatCurrency(netBillingAmount)}</PDFText>
              </PDFView>

              <PDFView style={styles.totalRow}>
                <PDFText style={styles.grandTotal}>GRAND TOTAL (OPD + Billing):</PDFText>
                <PDFText style={styles.grandTotal}>{formatCurrency(grandTotal)}</PDFText>
              </PDFView>
            </PDFView>

            {/* Footer */}
            <PDFView style={styles.footer}>
              <PDFText>This is a computer generated document. No signature required.</PDFText>
              <PDFText style={{ marginTop: 3, fontStyle: 'italic' }}>
                Report generated by Hospital Management System • Page 1 of 1
              </PDFText>
            </PDFView>
          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default OpdPatientLedgerPdf;